'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';

import { clientAxios } from '@/shared/api';
import {
  ApiResponse,
  AuthorListCardProps,
  SimpleAuthorInfo,
} from '@/shared/types';
import { useAuth } from '@/shared/hooks';
import { PATH } from '@/shared/constants';

interface AuthorListMutationParams {
  count: number;
  maxPage: number;
  authors: AuthorListCardProps[];
}

const useAuthorSubscription = (page?: number) => {
  const queryClient = useQueryClient();
  const { isAuth } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  return useMutation({
    mutationFn: (authorId: number) => {
      return clientAxios.post<ApiResponse<unknown>>('/authorSubscriptionApi/', {
        authorId,
      });
    },
    onMutate: async (authorId) => {
      if (!isAuth) {
        return router.push(`${PATH.AUTH_LOGIN}?next=${pathname}`);
      }

      await queryClient.cancelQueries({
        queryKey: ['authorInfoApi', authorId],
      });
      const previousAuthorInfo = queryClient.getQueryData([
        'authorInfoApi',
        authorId,
      ]);

      queryClient.setQueryData(
        ['authorInfoApi', authorId],
        (oldData: SimpleAuthorInfo) => {
          if (!oldData) return;

          return {
            ...oldData,
            isSubscribed: !oldData.isSubscribed,
          };
        }
      );

      if (!page) return;

      await queryClient.cancelQueries({ queryKey: ['authorList'] });

      const previousData = queryClient.getQueryData(['authorList', page, null]);
      queryClient.setQueryData(
        ['authorList', page, null],
        (oldData: AuthorListMutationParams) => {
          return {
            ...oldData,
            authors: oldData.authors.map((author) => {
              if (author.id === authorId) {
                return {
                  ...author,
                  isSubscribed: !author.isSubscribed,
                };
              }
              return author;
            }),
          };
        }
      );

      return { previousData, previousAuthorInfo };
    },
    onError: async (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['authorList'],
      });
      queryClient.invalidateQueries({
        queryKey: ['authorInfoApi', variables],
      });
    },
    onSettled: async (data, error, variables) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['authorList'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['author'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['authorInfoApi', variables],
        }),
      ]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getProfileTutorialStatusApi'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActivitySummaryApi'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActionLogApi', true],
      });
    },
  });
};

export default useAuthorSubscription;
